  import React, { Fragment, useState, useRef, useEffect } from "react";
  import { Row, Table } from "reactstrap";
  import {
    useReactTable,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    flexRender,
  } from "@tanstack/react-table";
  import { FadeLoader } from "react-spinners";
  import Select from "react-select";
  import { useDispatch, useSelector } from "react-redux";
  import { DebouncedInput } from "helpers/common_helper";
  import Pagination from "components/Common/Pagination";
  import { fetchStudentsRequest, fetchCoursesRequest, fetchCoursesDropdownRequest } from "store/actions";

  const StudentDataTable = ({
    columns,
    data,
    tableClass,
    isPagination = true,
    isGlobalFilter,
    SearchPlaceholder,
    totalrows,
    loading,
    docName = "Students",
    selectedSortData,
    setSelectedSortData,
    pageIndex,
    setPageIndex,
    pageSize,
    setPageSize,
    searchString,
    setSearchString,
  }) => {
    const dispatch = useDispatch();
    const tableRef = useRef();

    //  Get courses directly from Redux store
  const courses = useSelector((state) => state.Course?.dropdownCourses?.data || []);


  console.log("Courses from Redux:", courses);

    const [selectedCourse, setSelectedCourse] = useState(null);

    //  Fetch all courses once
useEffect(() => {
  dispatch(fetchCoursesDropdownRequest());
}, [dispatch]);


    //  React Table setup
    const table = useReactTable({
      columns,
      data,
      getCoreRowModel: getCoreRowModel(),
      getFilteredRowModel: getFilteredRowModel(),
      getPaginationRowModel: getPaginationRowModel(),
      getSortedRowModel: getSortedRowModel(),
    });


    const serialNumberColumn = {
      header: "#",
      id: "serial",
      cell: ({ row }) => (pageIndex - 1) * pageSize + row.index + 1,
      enableSorting: false,
    };

    table.setOptions((prev) => ({
      ...prev,
      columns: [serialNumberColumn, ...columns],
    }));

    const totalPages = Math.ceil(totalrows / pageSize);

    //  Fetch students when filters/pagination/search/course change
    useEffect(() => {
      if (loading) return;

      const params = {
        page: pageIndex,
        limit: pageSize,
      };

      if (selectedCourse) {
        params.courseId = selectedCourse.value;
      }

      if (selectedSortData?.value && selectedSortData?.direction) {
        params.sortBy = `${selectedSortData.value}:${selectedSortData.direction}`;
      }

      if (searchString) {
        params.search = searchString;
      }

      dispatch(fetchStudentsRequest(params));
    }, [selectedSortData, pageIndex, searchString, selectedCourse]);

    // Reset pagination when search or filter changes
    useEffect(() => {
      setPageIndex(1);
    }, [searchString, selectedCourse]);

    const handlePageChange = (newPageIndex) => setPageIndex(newPageIndex);

    return (
      <Fragment>
        <Row className="mb-2">
          {isGlobalFilter && (
            <>
              <div className="d-flex justify-content-between gap-2 mb-2">
                {/*  Filter Section */}
                <div className="d-flex justify-content-between gap-2 align-items-center">
                  <div style={{ maxWidth: "300px", minWidth: "200px" }}>
  <Select
    options={courses.map((c) => ({
      label: c.title,
      value: c._id,
    }))}
    value={selectedCourse}
    onChange={setSelectedCourse}
    placeholder="Filter by Course"
    isClearable
  />

                  </div>
                </div>

                {/*  Search Section */}
                <div className="d-flex justify-content-between align-items-end">
                  <DebouncedInput
                    value={searchString ?? ""}
                    onChange={(value) => setSearchString(String(value))}
                    className="form-control search-box me-2 d-inline-block"
                    placeholder={SearchPlaceholder}
                  />
                </div>
              </div>
            </>
          )}
        </Row>

        {/*  Data Table */}
        <div className="table-responsive" id="table-to-print" ref={tableRef}>
          <Table hover className={tableClass} bordered>
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) =>
                    !header.isPlaceholder ? (
                      <th key={header.id} style={{ width: "150px", verticalAlign: "middle" }}>
                        <div className="d-flex align-items-center justify-content-between">
                          <span>{flexRender(header.column.columnDef.header, header.getContext())}</span>
  {(header.column.columnDef.accessorKey &&
    header.column.columnDef.showFilter !== false) && (
    <span
                              className="ms-1"
                              style={{ cursor: "pointer" }}
                              onClick={(e) => {
                                e.stopPropagation();
                                const field = header.column.columnDef.accessorKey;
                                if (!field) return;

                                if (!selectedSortData || selectedSortData.value !== field) {
                                  setSelectedSortData({ value: field, direction: "asc" });
                                } else if (selectedSortData.direction === "asc") {
                                  setSelectedSortData({ value: field, direction: "desc" });
                                } else {
                                  setSelectedSortData({ value: field, direction: "asc" });
                                }
                              }}
                            >
                              {selectedSortData?.value === header.column.columnDef.accessorKey ? (
                                selectedSortData.direction === "asc" ? (
                                  <i className="mdi mdi-arrow-up" style={{ fontSize: "20px" }}></i>
                                ) : (
                                  <i className="mdi mdi-arrow-down" style={{ fontSize: "20px" }}></i>
                                )
                              ) : (
                                <i className="mdi mdi-swap-vertical" style={{ fontSize: "20px" }}></i>
                              )}
                            </span>
                          )}
                        </div>
                      </th>
                    ) : null
                  )}
                </tr>
              ))}
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={columns.length + 1} className="text-center border-none">
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "100px",
                      }}
                    >
                      <FadeLoader color="#00a895" size={40} />
                    </div>
                  </td>
                </tr>
              ) : data?.length <= 0 || table.getRowModel()?.rows?.length === 0 ? (
                <tr>
                  <td colSpan={columns.length + 1} className="text-center">
                    No Data Found
                  </td>
                </tr>
              ) : (
                table.getRowModel().rows.map((row) => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} style={{ verticalAlign: "middle" }}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>

        {/*  Pagination */}
        {isPagination && totalrows > 0 && (
          <Pagination
            currentPage={pageIndex}
            totalPages={totalPages}
            totalItems={totalrows}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            showInfo={true}
          />
        )}
      </Fragment>
    );
  };

  export default StudentDataTable;
